import React, {useMemo, useState} from 'react'
import {Box, Typography} from '@mui/material'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import FolderIcon from '@mui/icons-material/Folder'
import TabBar from 'src/components-new/layout/TabBar'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {useAppSelector} from 'src/hooks'
import SharedDocumentsTab from './SharedDocumentsTab'
import PersonalDocumentsTab from './PersonalDocumentsTab'

type TabKey = 'shared' | 'personal'

// Icone rimappate su @mui/icons-material dalle @mdi/js del legacy MyDocuments.tsx
// (mdiFolderAccount, mdiFolder), per restare coerenti con lo stack icone della nuova UI
// (vedi lo stesso approccio in PatientDetailNew.tsx). Le icone restano fisse per key,
// solo visibilità e label sono configurabili da PSP.
const TAB_ICONS: Record<TabKey, React.ReactElement> = {
  shared: <FolderSharedIcon fontSize="small" />,
  personal: <FolderIcon fontSize="small" />,
}

const DEFAULT_LABELS: Record<TabKey, string> = {
  shared: 'Documenti condivisi',
  personal: 'Documenti personali',
}

interface TabDef {
  key: TabKey
  label: string
  icon: React.ReactElement
}

export default function MyDocumentsNew(): JSX.Element {
  const sidebarItems = useAppSelector(
    (state) => state.pspConfigReducer.data.sidebarItems
  )

  const tabs = useMemo<TabDef[]>(() => {
    const sharedConfig = sidebarItems?.sharedDocuments
    const personalConfig = sidebarItems?.personalDocuments

    // `show !== false` (anziché `Boolean(show)`, usato da TherapiesNew/EnrollmentHubNew per le
    // loro sotto-tab) per retrocompatibilità: queste due tab sono già live per i PSP esistenti,
    // che non hanno ancora `sidebarItems.sharedDocuments`/`.personalDocuments` in config finché
    // non viene risalvata dal configuratore. Con `Boolean(show)` sparirebbero per tutti finché
    // qualcuno non aggiorna esplicitamente ogni PSP.
    const allTabs: (TabDef & {show: boolean})[] = [
      {
        key: 'shared',
        label: sharedConfig?.label ?? DEFAULT_LABELS.shared,
        icon: TAB_ICONS.shared,
        show: sharedConfig?.show !== false,
      },
      {
        key: 'personal',
        label: personalConfig?.label ?? DEFAULT_LABELS.personal,
        icon: TAB_ICONS.personal,
        show: personalConfig?.show !== false,
      },
    ]

    const visibleTabs = allTabs.filter((tab) => tab.show)

    // Safety net: se una config errata nasconde esplicitamente entrambe le tab,
    // mostriamo comunque entrambe per non lasciare la pagina vuota/rotta.
    if (visibleTabs.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        'MyDocumentsNew: sidebarItems nasconde entrambe le tab (sharedDocuments.show e personalDocuments.show sono entrambi false). Mostro comunque entrambe le tab come fallback di sicurezza.'
      )
      return allTabs
    }

    return visibleTabs
  }, [sidebarItems])

  const [activeTab, setActiveTab] = useState<TabKey>(tabs[0].key)

  // Se la config cambia dopo il mount e la tab attiva non è più tra quelle visibili,
  // riallinea alla prima disponibile invece di renderizzare una tab inesistente.
  const currentTab = tabs.some((tab) => tab.key === activeTab) ? activeTab : tabs[0].key

  const singleTab = tabs.length === 1 ? tabs[0] : undefined

  return (
    <Box>
      {tabs.length > 1 && (
        <Box sx={{borderBottom: '1px solid', borderColor: 'divider', mb: 3}}>
          <TabBar
            tabs={tabs}
            activeKey={currentTab}
            onChange={(key) => setActiveTab(key as TabKey)}
          />
        </Box>
      )}

      {singleTab && (
        // Con una sola tab visibile la TabBar (che fungerebbe anche da indicatore) non
        // viene renderizzata: senza questo titolo l'utente non saprebbe se sta guardando
        // documenti condivisi o personali.
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 3}}>
          {singleTab.icon}
          <Typography variant="h3" sx={{color: NEUTRAL_SCALE[900], fontWeight: 600, fontSize: 16}}>
            {singleTab.label}
          </Typography>
        </Box>
      )}

      {currentTab === 'shared' ? <SharedDocumentsTab /> : <PersonalDocumentsTab />}
    </Box>
  )
}
